// todo: add phone number to the object that represents our patients in the backend
// todo: re-add the edit and delete buttons with proper functionality.

import React, { useContext, useEffect, useState } from 'react'

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from '@mui/material'
import styles from './AllPatientsTable.module.scss'
import { epochToDDMMYY, capitalize, getAlerts } from '@utils/functions'
import { CurrentOrganizationContext } from '@context/CurrentOrganization'
import { UserContext } from '@context/UserContext'
import { useNavigate } from 'react-router-dom'
import ClinicvuService from 'services/Clinicvu/Clinicvu.service'
import PatientAlerts from './patient-alerts/PatientAlerts'

const Colors = {
  light: {
    green: '#4CAF50',
    televu_blue: '#0096FF',
    yellow: '#FFEB3B',
    orange: '#FF9800',
    red: '#F44336'
  }
}

const getAdherenceColor = (adherence) => {
  const value = parseInt(adherence)
  if (value >= 90) return Colors.light.green
  if (value >= 75) return Colors.light.televu_blue
  if (value >= 50) return Colors.light.yellow
  if (value >= 25) return Colors.light.orange
  return Colors.light.red
}

const AllPatientsTable = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPatients, setCurrentPatients] = useState([])

  const navigate = useNavigate()

  const { currentOrganization } = useContext(CurrentOrganizationContext)
  const { me } = useContext(UserContext)

  const [patients, setPatients] = useState([])

  const fetchPatients = async () => {
    if (me) {
      try {
        const res = await ClinicvuService.getPatientsOnOrganization(
          currentOrganization,
          searchTerm
        )
        if (res.status !== 200) {
          throw new Error(
            `Was not able to fetch all patients on portal ${currentOrganization}`
          )
        }
        setPatients(res.data)
      } catch (error) {
        console.error(error)
      }
    }
  }

  useEffect(() => {
    fetchPatients()
  }, [currentOrganization, searchTerm, me])

  useEffect(() => {
    setCurrentPatients(
      patients?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ||
        []
    )
  }, [patients, page, rowsPerPage])

  const handlePageChange = (e, newPage) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  return (
    <div className={styles.container}>
      {process.env.REACT_APP_environment_type === 'clinicvu' && (
        <div className={styles['top-section__search']}>
          <img
            src="/assets/svgs/search.svg"
            alt="Search"
            className={styles['top-section__search-icon']}
          />
          <input
            type="text"
            placeholder="Search"
            className={styles['top-section__search-input']}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #dce3ea',
          boxShadow: '0 8px 24px rgba(18, 37, 56, 0.09)'
        }}
      >
        <Table
          stickyHeader
          aria-label="All Patients Table"
          className={styles.table}
        >
          <TableHead className={styles.table__head}>
            <TableRow>
              <TableCell sx={{ fontSize: '1rem' }}>Name</TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>Date of Birth</TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>Sex</TableCell>
              {process.env.REACT_APP_environment_type === 'clinicvu'
                ? (
                <>
                  <TableCell sx={{ fontSize: '1rem' }}>
                    <Tooltip
                      title={
                        <div>
                          <div
                            style={{ fontWeight: 'bold', marginBottom: '5px' }}
                          >
                            Care Plan Compliance Legend:
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <div
                              style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: Colors.light.green
                              }}
                            ></div>
                            <span>90-100: Excellent</span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <div
                              style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: Colors.light.televu_blue
                              }}
                            ></div>
                            <span>75-89: Good</span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <div
                              style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: Colors.light.yellow
                              }}
                            ></div>
                            <span>50-74: Average</span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <div
                              style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: Colors.light.orange
                              }}
                            ></div>
                            <span>25-49: Below Average</span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <div
                              style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: Colors.light.red
                              }}
                            ></div>
                            <span>0-24: Poor</span>
                          </div>
                        </div>
                      }
                      arrow
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                      >
                        Care Plan Compliance{' '}
                        <span style={{ fontSize: '16px', cursor: 'help' }}>
                          ⓘ
                        </span>
                      </div>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: '1rem' }}>Alerts</TableCell>
                </>
                  )
                : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {process.env.REACT_APP_environment_type === 'clinicvu' &&
              currentPatients.map((patient, index) => (
                <TableRow
                  className={styles.tablerow}
                  key={patient.resource.id + '-' + index}
                >
                  <TableCell
                    onClick={() => navigate(`/patient/${patient.resource.id}`)}
                  >
                    <div className={styles.username}>
                      <img
                        src="assets/user-icon.jpeg"
                        alt={`User ${patient.resource.name[0].text}`}
                        className={styles['user-icon']}
                      />
                      <span className={styles.username__text}>
                        {patient.resource.name[0].given.join(' ') +
                          ' ' +
                          patient.resource.name[0].family}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    onClick={() => navigate(`/patient/${patient.resource.id}`)}
                  >
                    {epochToDDMMYY(
                      new Date(patient.resource.birthDate).getTime()
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() => navigate(`/patient/${patient.resource.id}`)}
                  >
                    {capitalize(patient.resource.gender)}
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        <div>
                          <div>Compliance Score:</div>
                          <div>90-100: Excellent</div>
                          <div>75-89: Good</div>
                          <div>50-74: Average</div>
                          <div>25-49: Below Average</div>
                          <div>0-24: Poor</div>
                        </div>
                      }
                      arrow
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: getAdherenceColor(
                              patient.resource.compliance.score
                            )
                          }}
                        />
                        <span
                          style={{
                            color: getAdherenceColor(
                              patient.resource.compliance.score
                            ),
                            fontWeight: 'bold'
                          }}
                        >
                          {patient.resource.compliance.score}%
                        </span>
                      </div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <PatientAlerts
                      alerts={getAlerts(patient)}
                      patientId={patient.resource.id}
                    />
                  </TableCell>
                </TableRow>
              ))}
            {process.env.REACT_APP_environment_type === 'biovu' &&
              currentPatients.map((patient, index) => (
                <TableRow
                  className={styles.tablerow}
                  key={patient.resource.id + '-' + index}
                  onClick={() => navigate(`/user/${patient.resource.id}`)}
                >
                  <TableCell>
                    <div className={styles.username}>
                      <img
                        src="assets/user-icon.jpeg"
                        alt={`User ${patient.resource.name[0].text}`}
                        className={styles['user-icon']}
                      />
                      <span className={styles.username__text}>
                        {patient.resource.name[0].given.join(' ') +
                          ' ' +
                          patient.resource.name[0].family}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {epochToDDMMYY(
                      new Date(patient.resource.birthDate).getTime()
                    )}
                  </TableCell>
                  <TableCell>{capitalize(patient.resource.gender)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                page={page}
                onPageChange={handlePageChange}
                count={patients?.length || 0}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </div>
  )
}

export default AllPatientsTable
